import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { userApi, User, Tag, tagApi } from '../utils/api';
import { Event } from '../types/event';
import EventCard from '../components/EventCard';
import { getAvatarProps, AVAILABLE_SPORTS } from '../utils/userUtils';
import { useAuth } from '../contexts/AuthContext';

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [hostingEvents, setHostingEvents] = useState<Event[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Check if viewing own profile and redirect to /profile
    if (currentUser && userId === currentUser.id) {
      navigate('/profile', { replace: true });
      return;
    }
    
    // Load profile if we have a userId
    // Note: Backend endpoint requires authentication
    if (currentUser) {
      loadUserProfile();
    } else {
      // Wait a bit for auth to load, then try again
      const timer = setTimeout(() => {
        if (currentUser) {
          loadUserProfile();
        } else {
          setError('Please log in to view user profiles');
          setLoading(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [userId, currentUser, navigate]);

  const loadUserProfile = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading user profile for userId:', userId);

      // Load user profile first (required), then other data
      // Use Promise.allSettled to handle partial failures gracefully
      const [userProfileResult, eventsResult, tagsResult] = await Promise.allSettled([
        userApi.getUserProfile(userId),
        userApi.getUserHostingEvents(userId),
        tagApi.getTags()
      ]);

      // User profile is required - if it fails, show error
      if (userProfileResult.status === 'rejected') {
        throw userProfileResult.reason;
      }

      const userProfile = userProfileResult.value;
      
      // Normalize sports data structure to ensure consistency
      if (userProfile.sports && Array.isArray(userProfile.sports)) {
        userProfile.sports = userProfile.sports.map((sport: any) => {
          if (typeof sport === 'object' && sport !== null) {
            return {
              sport_name: sport.sport_name || sport.sportName || sport.name,
              skill_level: sport.skill_level || sport.skillLevel || sport.level,
              preferences: sport.preferences || []
            };
          }
          return sport;
        });
      }

      // Handle events and tags - if they fail, just log and continue with empty arrays
      const events = eventsResult.status === 'fulfilled' ? eventsResult.value : [];
      const allTags = tagsResult.status === 'fulfilled' ? tagsResult.value : [];

      if (eventsResult.status === 'rejected') {
        console.warn('Failed to load hosting events:', eventsResult.reason);
      }
      if (tagsResult.status === 'rejected') {
        console.warn('Failed to load tags:', tagsResult.reason);
      }

      setUser(userProfile);
      setHostingEvents(events);
      setTags(allTags);
    } catch (err: any) {
      let errorMessage = 'Failed to load user profile';
      
      // Handle different types of errors
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMessage = 'Network Error: Unable to connect to the server. Please check if the backend is running.';
      } else if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.detail || err.response.data?.message || `Server error: ${err.response.status}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error loading user profile:', err);
      
      // Log more details for debugging
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      } else if (err.request) {
        console.error('Request made but no response received:', err.request);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/event/${eventId}`);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#533B7D' }} />
        </Box>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mb: 3 }}
        >
          Back
        </Button>
        <Alert severity="error">
          {error || 'User not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackClick}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      {/* Profile Header */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Avatar
            {...getAvatarProps(user, 104)}
            sx={{
              width: 104,
              height: 104,
              backgroundColor: '#533B7D',
              fontSize: '2.5rem',
              ...getAvatarProps(user, 104).sx
            }}
          />

          <Box sx={{ pt: 1, flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
              {user.display_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Member since {new Date(user.created_at || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* About Me Section */}
      {user.bio && (
        <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            About me
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {user.bio}
          </Typography>
        </Paper>
      )}

      {/* Gallery Section */}
      {[user.gallery_image_1, user.gallery_image_2, user.gallery_image_3, user.gallery_image_4].filter(Boolean).length > 0 && (
        <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Gallery
          </Typography>

          <Grid container spacing={2}>
            {[
              user.gallery_image_1,
              user.gallery_image_2,
              user.gallery_image_3,
              user.gallery_image_4
            ].filter(Boolean).map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Box
                  component="img"
                  src={image || ''}
                  alt={`Gallery ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Sports Interests Section */}
      {user.sports && user.sports.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Sports interests
          </Typography>

          <Grid container spacing={2}>
            {user.sports.map((sport, index) => {
              const sportInfo = AVAILABLE_SPORTS.find(s => s.name === sport.sport_name) ||
                AVAILABLE_SPORTS.find(s => s.id === sport.sport_name.toLowerCase());

              // Get tag names from IDs
              const preferenceTags = (sport.preferences || [])
                .map(prefId => {
                  if (!prefId) return null;
                  const id = typeof prefId === 'object' ? (prefId as any).id : prefId;
                  if (!id) return null;
                  const tag = tags.find(t => t.id === id);
                  return tag ? tag.name : id;
                })
                .filter((tag): tag is string => tag !== null && tag !== undefined);

              const displayTags = preferenceTags.slice(0, 3);
              const remainingTags = preferenceTags.length - 3;

              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 24, mr: 1 }}>
                        {sportInfo?.icon || '🏅'}
                      </Typography>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sport.sport_name}
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          {sport.skill_level}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {displayTags.map((tagName, i) => (
                        <Chip
                          key={i}
                          label={tagName}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      ))}
                      {remainingTags > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                          +{remainingTags} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Hosting Events Section */}
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
          Hosting ({hostingEvents.length})
        </Typography>

        {hostingEvents.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {hostingEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => handleEventClick(event.id)}
                variant="horizontal"
                isHost={false}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No events hosted yet.
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default UserProfile;

